"use client";

import type { ComponentProps } from "react";
import { useLingui } from "@lingui/react/macro";
import { Button } from "@marketing-sites/ui/primitives";

/**
 * Nav signup CTA rendered as a hard <a> rather than the shared Header's
 * data-driven button. The React signup app (intercom.com/signup) is same-origin,
 * so the shared Header would route it through next/link and intercept the click —
 * bypassing the global AddUrlParamsToCrossSiteLinks forwarder that appends
 * utm_referrer by mutating the DOM href. A real anchor lets the forwarder run.
 *
 * Because this bypasses the shared Header's NavigationLink, we re-emit its
 * navItemClick event ourselves so the CTA logs the same "Main Nav" analytics as
 * the other nav links (see navigation-link.tsx).
 */
export function TrialCtaNavButton({
  href,
  label,
  analyticsLabel,
  buttonMetadata,
  eventMetadata,
  className,
}: {
  href: string;
  label?: string;
  analyticsLabel?: string;
  buttonMetadata: { analyticsId: string };
  eventMetadata: { analyticsId: string };
  className?: string;
}) {
  const { t } = useLingui();
  const resolvedLabel = label ?? t`Start free trial`;

  // Base UI types onClick for a button; hardNavigate renders an anchor.
  const dispatchNavItemClick: ComponentProps<typeof Button>["onClick"] = (
    e,
  ) => {
    const anchor = e.currentTarget as HTMLElement as HTMLAnchorElement;

    window.dispatchEvent(
      new CustomEvent("navItemClick", {
        detail: {
          text: analyticsLabel ?? resolvedLabel,
          // Read the DOM href so params the AddUrlParamsToCrossSiteLinks
          // forwarder appends during capture are in the analytics destination,
          // matching NavigationLink.
          destination: anchor.href,
          location: "Main Nav",
          analyticsId: eventMetadata.analyticsId,
        },
      }),
    );
  };

  return (
    <Button
      analyticsId={buttonMetadata.analyticsId}
      href={href}
      className={className}
      hardNavigate
      onClick={dispatchNavItemClick}
    >
      {resolvedLabel}
    </Button>
  );
}
